import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Stack,
  Alert,
  Table,
  Button,
  Dialog,
  Select,
  MenuItem,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  Container,
  Typography,
  InputLabel,
  FormControl,
  DialogTitle,
  DialogActions,
  DialogContent,
  TableContainer,
  CircularProgress,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { format, isValid } from 'date-fns';
import { tr } from 'date-fns/locale';
import toast from 'react-hot-toast';

import { toastApiError } from '../../../utils/errorMessage';

import { Label } from 'src/components/label';
import {
  overtimeApi,
  type OvertimeStatus,
  type OvertimeRequestDto,
  OVERTIME_STATUS_LABELS_TR,
  OVERTIME_STATUS_COLORS,
} from 'src/api/overtime.api';

// ----------------------------------------------------------------------

type StatusFilter = OvertimeStatus | 'ALL';

export function OvertimeView() {
  const [requests, setRequests] = useState<OvertimeRequestDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('PENDING');

  const [selected, setSelected] = useState<OvertimeRequestDto | null>(null);
  const [decision, setDecision] = useState<'APPROVED' | 'REJECTED' | null>(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const data = await overtimeApi.getOvertimeRequests(
        statusFilter === 'ALL' ? undefined : { status: statusFilter }
      );
      setRequests(data);
    } catch (error: any) {
      toast.error('Mesai beyanları yüklenirken hata oluştu');
      console.error('Error loading overtime requests:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const openDecisionDialog = (request: OvertimeRequestDto, type: 'APPROVED' | 'REJECTED') => {
    setSelected(request);
    setDecision(type);
    setNotes('');
  };

  const closeDialog = () => {
    if (submitting) return;
    setSelected(null);
    setDecision(null);
  };

  const handleConfirm = async () => {
    if (!selected || !decision) return;
    try {
      setSubmitting(true);
      if (decision === 'APPROVED') {
        await overtimeApi.approveOvertime(selected.id, notes.trim() || undefined);
        toast.success('Mesai beyanı onaylandı');
      } else {
        await overtimeApi.rejectOvertime(selected.id, notes.trim() || undefined);
        toast.success('Mesai beyanı reddedildi — mesai vardiya bitişine kırpıldı');
      }
      closeDialog();
      loadRequests();
    } catch (error: any) {
      toastApiError(error, 'İşlem sırasında hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  // FE-3: null guard vardı ama geçersiz (non-null bozuk) string date-fns'te RangeError atıyordu.
  const formatDateTime = (value: string | null) => {
    if (!value) return '—';
    const d = new Date(value);
    return isValid(d) ? format(d, 'dd MMM yyyy HH:mm', { locale: tr }) : '—';
  };

  return (
    <Container maxWidth="xl">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Mesai Onayları</Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={statusFilter}
              label="Durum"
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <MenuItem value="ALL">Tümü</MenuItem>
              {(Object.keys(OVERTIME_STATUS_LABELS_TR) as OvertimeStatus[]).map((status) => (
                <MenuItem key={status} value={status}>
                  {OVERTIME_STATUS_LABELS_TR[status]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="outlined" onClick={loadRequests}>
            Yenile
          </Button>
        </Stack>
      </Stack>

      <Card>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={240}>
            <CircularProgress />
          </Box>
        ) : requests.length === 0 ? (
          <Alert severity="info" sx={{ m: 3 }}>
            {statusFilter === 'PENDING'
              ? 'Bekleyen mesai beyanı bulunmuyor.'
              : 'Bu durumda mesai beyanı bulunmuyor.'}
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Çalışan</TableCell>
                  <TableCell>Şube</TableCell>
                  <TableCell>Vardiya Girişi</TableCell>
                  <TableCell>Beyan Edilen Bitiş</TableCell>
                  <TableCell>Sebep</TableCell>
                  <TableCell>Durum</TableCell>
                  <TableCell>İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>{request.employeeName || '—'}</TableCell>
                    <TableCell>{request.branchName || '—'}</TableCell>
                    <TableCell>{formatDateTime(request.checkInTime)}</TableCell>
                    <TableCell>
                      {request.declaredEndTime ? (
                        formatDateTime(request.declaredEndTime)
                      ) : (
                        <Label variant="soft" color="warning">
                          Belirsiz
                        </Label>
                      )}
                    </TableCell>
                    <TableCell>{request.reason || '—'}</TableCell>
                    <TableCell>
                      <Label variant="soft" color={OVERTIME_STATUS_COLORS[request.status]}>
                        {OVERTIME_STATUS_LABELS_TR[request.status]}
                      </Label>
                    </TableCell>
                    <TableCell>
                      {request.status === 'PENDING' ? (
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => openDecisionDialog(request, 'APPROVED')}
                          >
                            Onayla
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<CancelIcon />}
                            onClick={() => openDecisionDialog(request, 'REJECTED')}
                          >
                            Reddet
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {request.reviewNotes ||
                            (request.approvedAt ? formatDateTime(request.approvedAt) : '—')}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Decision confirm dialog */}
      <Dialog open={!!selected && !!decision} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {decision === 'APPROVED' ? 'Mesai Beyanı Onayı' : 'Mesai Beyanı Reddi'}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity={decision === 'APPROVED' ? 'info' : 'error'}>
                {decision === 'APPROVED'
                  ? `${selected.employeeName || 'Çalışan'} için ekstra mesai onaylanacak${
                      selected.declaredEndTime
                        ? ` (bitiş: ${formatDateTime(selected.declaredEndTime)})`
                        : ' (belirsiz bitiş)'
                    }.`
                  : 'Beyan reddedilecek; mesai, vardiya bitiş saatine kırpılır.'}
              </Alert>
              <TextField
                label="Not (İsteğe Bağlı)"
                multiline
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Vazgeç
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={decision === 'APPROVED' ? 'success' : 'error'}
            disabled={submitting}
          >
            {submitting ? 'İşleniyor...' : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
